/**
 * BODYMAX Google Play Billing Service
 * 
 * Uses the Digital Goods API + Payment Request API to handle
 * subscriptions purchased through the Google Play Store.
 * 
 * This only works inside a Trusted Web Activity (TWA) distributed via Google Play
 * with playBilling enabled in the Bubblewrap config.
 * 
 * Flow:
 *   1. getProductDetails() — fetch price/title from Play
 *   2. purchaseSubscription() — launch Play purchase flow via Payment Request
 *   3. Backend validates purchaseToken via Google Play Developer API
 *   4. Backend acknowledges the purchase
 *   5. Frontend receives entitlement confirmation
 */

const PLAY_BILLING_URL = 'https://play.google.com/billing';

// TODO: Set this to your actual Play Console subscription product ID
const PRODUCT_ID = '1_m';

let _digitalGoodsService = null;

/**
 * Get or create the Digital Goods Service connection.
 * Returns null if not available (not in TWA / Play Billing not enabled).
 */
async function getService() {
  if (_digitalGoodsService) return _digitalGoodsService;
  
  if (!('getDigitalGoodsService' in window)) {
    console.log('[PlayBilling] Digital Goods API not available');
    return null;
  }
  
  try {
    _digitalGoodsService = await window.getDigitalGoodsService(PLAY_BILLING_URL);
    console.log('[PlayBilling] Connected to Play Billing service');
    return _digitalGoodsService;
  } catch (e) {
    console.error('[PlayBilling] Failed to connect:', e);
    return null;
  }
}

/**
 * Fetch subscription product details (price, title, description) from Play.
 * Returns null if unavailable.
 */
export async function getProductDetails(productId = PRODUCT_ID) {
  const service = await getService();
  if (!service) return null;
  
  try {
    const details = await service.getDetails([productId]);
    if (details && details.length > 0) {
      const item = details[0];
      console.log('[PlayBilling] Product details:', item);
      return {
        productId: item.itemId,
        title: item.title,
        description: item.description,
        price: item.price, // { currency: 'EUR', value: '9.99' }
      };
    }
    return null;
  } catch (e) {
    console.error('[PlayBilling] Failed to get product details:', e);
    return null;
  }
}

/**
 * Format a Digital Goods price ({ currency, value }) for display.
 * Returns null when there is nothing usable to show.
 */
export function formatPlayPrice(price) {
  if (!price) return null;
  if (typeof price === 'string') return price;
  const value = Number(price.value);
  if (!price.value || Number.isNaN(value)) return null;
  const currency = price.currency || 'EUR';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch (e) {
    return `${value} ${currency}`;
  }
}

/**
 * Check existing Play purchases (e.g. for restore/re-login).
 * Returns array of existing purchase tokens.
 */
export async function listExistingPurchases() {
  const service = await getService();
  if (!service) return [];
  
  try {
    const purchases = await service.listPurchases();
    console.log('[PlayBilling] Existing purchases:', purchases.length);
    return purchases.map(p => ({
      itemId: p.itemId,
      purchaseToken: p.purchaseToken,
    }));
  } catch (e) {
    console.error('[PlayBilling] Failed to list purchases:', e);
    return [];
  }
}

/**
 * Launch the Google Play purchase flow for the BODYMAX subscription.
 * 
 * Uses Payment Request API with the 'https://play.google.com/billing' method.
 * Returns { success, purchaseToken } on completion.
 */
export async function purchaseSubscription() {
  const details = await getProductDetails();
  if (!details) {
    return { success: false, error: 'Product not available' };
  }
  
  try {
    const paymentMethod = [{
      supportedMethods: PLAY_BILLING_URL,
      data: {
        sku: PRODUCT_ID,
      },
    }];
    
    const paymentDetails = {
      total: {
        label: details.title || 'BODYMAX Premium',
        amount: details.price || { currency: 'EUR', value: '9.99' },
      },
    };
    
    const request = new PaymentRequest(paymentMethod, paymentDetails);
    const response = await request.show();
    
    // Extract purchase token from the response
    const { purchaseToken } = response.details;
    
    if (!purchaseToken) {
      await response.complete('fail');
      return { success: false, error: 'No purchase token received' };
    }
    
    console.log('[PlayBilling] Purchase token received:', purchaseToken.slice(0, 20) + '...');
    
    // Complete the payment UI
    await response.complete('success');
    
    return {
      success: true,
      purchaseToken,
      productId: PRODUCT_ID,
    };
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('[PlayBilling] Purchase cancelled by user');
      return { success: false, error: 'cancelled' };
    }
    console.error('[PlayBilling] Purchase failed:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Consume/acknowledge a purchase after backend validation.
 * Called by the Digital Goods API to confirm the purchase was processed.
 */
export async function acknowledgePurchase(purchaseToken) {
  const service = await getService();
  if (!service) return false;
  
  try {
    await service.consume(purchaseToken);
    console.log('[PlayBilling] Purchase acknowledged');
    return true;
  } catch (e) {
    // For subscriptions, consume may not be needed (only for one-time purchases)
    // Acknowledgement is handled server-side via Google Play Developer API
    console.log('[PlayBilling] Acknowledge via Digital Goods not needed for subscriptions');
    return true;
  }
}
